import { useState } from "react";
import { removeFriend } from "../api/users";
import { calcSimilarity, getMergedFriendIds } from "../utils/similarity";
import UniverseFeed from "./UniverseFeed";
import "./FriendsScreen.css";
import { readBooks } from "../utils/bookState";

const FriendsScreen = ({ onBack, onUserClick, onBookClick, onStoryClick, onQuoteClick, publicQuotes = [], myBooks, allBooks = [], autoFriends, myFriends = [], users = [], currentUser, reloadFriends }) => {
  /* 이 화면의 첫 얼굴은 '지금 무슨 일이 있었나' 다. 겹치는 책·궤도는
     구조를 보는 눈이고, 사람은 소식을 먼저 본다. */
  const [view, setView] = useState("feed");

  const myId = currentUser?.uid;
  const iAddedIds = new Set(myFriends);
  const addedMeIds = new Set(
    (users || []).filter(u => u.id !== myId && (u.manualFriends || []).includes(myId)).map(u => u.id)
  );
  const allIds = getMergedFriendIds(autoFriends, myFriends, users, myId);

  // 담아둔 책은 공통 책으로 세지 않는다.
  const myReadBooks = readBooks(myBooks, currentUser?.uid);
  const myBookIds = myReadBooks.map(b => b.id);
  const allFriends = [...allIds].map(id => {
    const auto = autoFriends.find(f => f.id === id);
    const user = (users || []).find(u => u.id === id);
    const base = auto || user || {};
    const similarity = auto?.similarity || calcSimilarity(myBookIds, (user?.bookIds || []));
    const iAdded = iAddedIds.has(id);
    const theyAdded = addedMeIds.has(id);
    let friendType;
    if (auto) friendType = "auto";
    else if (iAdded && theyAdded) friendType = "mutual";
    else if (iAdded) friendType = "i-added";
    else friendType = "added-me";
    return { ...base, id, similarity, friendType };
  }).filter(f => f.name).sort((a, b) =>
    // 1순위: 취향 유사도. 겹치는 사람이 안쪽 궤도로 온다.
    b.similarity - a.similarity ||
    // 2순위: 읽은 책 수. 유사도가 같으면(특히 0% 끼리) 활발한 행성이 가까이.
    //        기준이 하나뿐이면 동점일 때 순서가 무작위라 볼 때마다 자리가 바뀐다.
    (b.bookIds?.length || 0) - (a.bookIds?.length || 0) ||
    // 3순위: 이름. 위 둘까지 같아도 순서가 고정된다.
    (a.name || "").localeCompare(b.name || "")
  );
  const totalCount = allFriends.length;

  const MAX_ORBIT = 6;
  const orbitFriends = allFriends.slice(0, MAX_ORBIT);
  const extraCount = totalCount - MAX_ORBIT;

  // 3 tiers: inner(0-1), middle(2-3), outer(4-5)
  const ORBIT_TIERS = [
    { radius: 100, duration: 20, size: 56 },
    { radius: 100, duration: 20, size: 52 },
    { radius: 150, duration: 26, size: 48 },
    { radius: 150, duration: 26, size: 46 },
    { radius: 195, duration: 32, size: 42 },
    { radius: 195, duration: 32, size: 40 },
  ];

  const handleRemoveFriend = async (userId) => {
    if (!currentUser) return;
    await removeFriend(currentUser.uid, userId);
    if (reloadFriends) reloadFriends();
  };

  return (
    <div className="bp-layer bp-layer-full">
      <div className="back-bar" onClick={onBack}>← <span>돌아가기</span></div>

      <div className="friends-header">
        <div className="friends-section-label">
          이웃 행성 · {totalCount}명
        </div>
        <div className="friends-tabs">
          <button className={`friends-tab${view==="feed"?" active":""}`} onClick={()=>setView("feed")}>✦ 이야기</button>
          <button className={`friends-tab${view==="universe"?" active":""}`} onClick={()=>setView("universe")}>🪐 궤도</button>
          <button className={`friends-tab${view==="list"?" active":""}`} onClick={()=>setView("list")}>☰ 목록</button>
        </div>
      </div>

      {/* 앞 두 줄은 두 뷰 모두에 해당하고, 궤도 거리 설명은 우주 뷰 전용이다. */}
      <div className="friends-hint">
        {view === "feed" ? (
          <>
            <div>이웃 행성들과 내 기록을 <b>최근 순</b>으로 모았어요</div>
            <div>카드를 누르면 그 책으로, 이름을 누르면 그 행성으로 갑니다</div>
          </>
        ) : (
          <>
            <div>읽은 책이 <b>30% 이상</b> 겹치면 자동으로 이웃이 되고, 직접 추가할 수도 있어요</div>
            <div>취향 %는 내가 읽은 책 중 그 사람과 겹치는 비율이에요</div>
          </>
        )}
        {view === "universe" && (
          <div className="friends-hint-orbit">
            {totalCount > MAX_ORBIT
              ? <>취향이 가장 많이 겹치는 <b>{MAX_ORBIT}명</b>이 가까운 궤도부터 돕니다</>
              : <>취향이 많이 겹칠수록 <b>가까운 궤도</b>를 돕니다</>}
          </div>
        )}
      </div>

      {view === "feed" && (
        <UniverseFeed
          allBooks={allBooks}
          users={users}
          uid={myId}
          quotes={publicQuotes}
          onBookClick={onBookClick}
          onUserClick={onUserClick}
          onStoryClick={onStoryClick}
          onQuoteClick={onQuoteClick}
        />
      )}

      {view === "universe" && (
        <div className="friend-universe">
          <div className="friend-orbits">
            {[200, 300, 390].map((d, i) => (
              <div key={i} className="friend-orbit" style={{ width:d, height:d, marginLeft:-d/2, marginTop:-d/2, top:"50%", left:"50%", position:"absolute" }}/>
            ))}
          </div>

          <div className="friends-center">
            <div className="friends-center-orb">🌿</div>
            <div className="friends-center-label">나</div>
          </div>

          <div className="friend-planets-container">
            {orbitFriends.map((r, i) => {
              const tier = ORBIT_TIERS[i] || ORBIT_TIERS[ORBIT_TIERS.length - 1];
              const size = tier.size;
              const [c1,c2,c3] = r.planetColors || ["#7b6cd4","#3a2d7a","#1a1040"];
              const friendBooks = myReadBooks.filter(b => (r.bookIds || []).includes(b.id));
              // Same tier planets get different starting angles
              const posInTier = i % 2;
              const startOffset = posInTier === 0 ? 0 : -(tier.duration / 2);
              return (
                <div
                  key={r.id}
                  className="friend-planet-wrap"
                  style={{
                    marginLeft: -size / 2,
                    marginTop: -size / 2,
                    animation: `orbitDynamic ${tier.duration}s linear infinite`,
                    animationDelay: `${startOffset}s`,
                    "--orbit-radius": `${tier.radius}px`,
                  }}
                >
                  <div onClick={() => onUserClick(r.id)} className="friend-planet-clickable">
                    <div className="friend-planet" style={{
                      width:size, height:size,
                      background:`radial-gradient(circle at 35% 30%,${c1},${c2} 50%,${c3})`,
                      fontSize: size > 50 ? "1.4rem" : "1.1rem",
                    }}>
                      {r.avatar}
                    </div>
                    <div className="friend-name-tag">
                      <div>{r.name}</div>
                      {r.friendType === "auto"
                        ? <div className="friend-similarity">취향 {r.similarity}%</div>
                        : r.friendType === "mutual"
                          ? <div className="friend-similarity mutual">서로 추가 · 취향 {r.similarity}%</div>
                          : r.friendType === "i-added"
                            ? <div className="friend-similarity manual">내가 추가 · 취향 {r.similarity}%</div>
                            : <div className="friend-similarity added-me">나를 추가 · 취향 {r.similarity}%</div>
                      }
                      <div className="friend-book-count">{friendBooks.length}권</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {extraCount > 0 && (
            <button className="btn-orbit-more" onClick={() => setView("list")}>
              +{extraCount}명 더 보기 →
            </button>
          )}
        </div>
      )}

      {view === "list" && (
        <div className="friend-list">
          {allFriends.map((r, i) => {
            const [c1,,c3] = r.planetColors || ["#7b6cd4","#1a1040"];
            const friendBooks = myReadBooks.filter(b => (r.bookIds || []).includes(b.id));
            const sharedBooks = friendBooks.filter(b => myReadBooks.some(mb => mb.title === b.title));
            return (
              <div key={r.id} className="friend-list-item" style={{ animationDelay:`${i*0.08}s` }} onClick={() => onUserClick(r.id)}>
                <div className="friend-planet-mini" style={{
                  width:48, height:48,
                  background:`radial-gradient(circle at 35% 30%,${c1},${c3})`,
                }}>
                  {r.avatar}
                </div>
                <div className="friend-info">
                  <div className="friend-info-name">{r.name}</div>
                  <div className="friend-info-handle">@{r.handle}</div>
                  <div className="friend-info-books">
                    📚 {friendBooks.length}권 읽음 · 🤝 나와 {sharedBooks.length}권 공통
                  </div>
                  {r.friendType === "mutual" && (
                    <span className="friend-manual-badge mutual">서로 추가</span>
                  )}
                  {r.friendType === "i-added" && (
                    <span className="friend-manual-badge">내가 추가</span>
                  )}
                  {r.friendType === "added-me" && (
                    <span className="friend-manual-badge added-me">나를 추가</span>
                  )}
                </div>
                <div className="friend-stats-col">
                  <div className="friend-stats-pct">{r.similarity}%</div>
                  <div className="friend-stats-label">취향 유사도</div>
                  <div className="friend-stats-bar">
                    <div className="friend-stats-bar-fill" style={{ width:`${r.similarity}%` }}/>
                  </div>
                  {(r.friendType === "i-added" || r.friendType === "mutual") && (
                    <button className="btn-remove-friend" onClick={(e) => { e.stopPropagation(); handleRemoveFriend(r.id); }}>
                      해제
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FriendsScreen;
